import { request } from "../../../services/api"
import { createFormDataNested } from "../../../utils/form-data/create-form-data-nested"
import type { ProjectTypeTemplatePhase } from "../types/project-type.types.ts"
import type {
  BulkUpdatePhasesData,
  PhaseCopyData,
  PhaseCreateData,
  PhaseListParams,
  PhaseListResponse,
  PhasePartialUpdateData,
  PhaseUpdateData,
  TemplateApiResponse,
  TemplateEntityId
} from "./services.types.ts"

const PHASES_BASE_URL = "/templates-management/phases"

const PATHS = {
  // Phases
  getPhases: `${PHASES_BASE_URL}/`,
  createPhase: `${PHASES_BASE_URL}/`,
  autocompletePhases: `${PHASES_BASE_URL}/autocomplete/`,
  getPhaseDetails: (id: TemplateEntityId) => `${PHASES_BASE_URL}/${id}/`,
  updatePhase: (id: TemplateEntityId) => `${PHASES_BASE_URL}/${id}/`,
  partialUpdatePhase: (id: TemplateEntityId) => `${PHASES_BASE_URL}/${id}/`,
  deletePhase: (id: TemplateEntityId) => `${PHASES_BASE_URL}/${id}/`,
  copyPhase: (id: TemplateEntityId) => `${PHASES_BASE_URL}/${id}/copy/`,
  bulkUpdatePhases: `${PHASES_BASE_URL}/bulk_update/`
}

export const getPhases = (params?: PhaseListParams) =>
  request.api.get<PhaseListResponse, TemplateApiResponse<unknown>>(
    PATHS.getPhases,
    params
  )

export const createPhase = (data: PhaseCreateData) => {
  if (!data?.file_list?.length) {
    return request.api.post<
      TemplateApiResponse<ProjectTypeTemplatePhase>,
      TemplateApiResponse<unknown>
    >(PATHS.createPhase, data)
  }

  const headers = { "Content-Type": "multipart/form-data" }
  const formData = createFormDataNested(data, "file")

  return request.api.post<
    TemplateApiResponse<ProjectTypeTemplatePhase>,
    TemplateApiResponse<unknown>
  >(PATHS.createPhase, formData, { headers })
}

export const autocompletePhases = (params?: PhaseListParams) =>
  request.api.get<ProjectTypeTemplatePhase[], TemplateApiResponse<unknown>>(
    PATHS.autocompletePhases,
    params
  )

export const getPhaseDetails = (phaseId: TemplateEntityId) =>
  request.api.get<ProjectTypeTemplatePhase, TemplateApiResponse<unknown>>(
    PATHS.getPhaseDetails(phaseId)
  )

export const updatePhase = ({ id, ...data }: PhaseUpdateData) => {
  if (!data?.file_list?.length) {
    return request.api.put<
      TemplateApiResponse<ProjectTypeTemplatePhase>,
      TemplateApiResponse<unknown>
    >(PATHS.updatePhase(id), data)
  }

  const headers = { "Content-Type": "multipart/form-data" }
  const formData = createFormDataNested(data, "file")

  return request.api.put<
    TemplateApiResponse<ProjectTypeTemplatePhase>,
    TemplateApiResponse<unknown>
  >(PATHS.updatePhase(id), formData, { headers })
}

export const partialUpdatePhase = ({ id, ...data }: PhasePartialUpdateData) => {
  return request.api.patch<
    TemplateApiResponse<ProjectTypeTemplatePhase>,
    TemplateApiResponse<unknown>
  >(PATHS.partialUpdatePhase(id), data)
}

export const deletePhase = (id: TemplateEntityId) =>
  request.api.delete<TemplateApiResponse<null>, TemplateApiResponse<unknown>>(
    PATHS.deletePhase(id)
  )

export const copyPhase = (id: TemplateEntityId, data: PhaseCopyData) =>
  request.api.post<
    TemplateApiResponse<ProjectTypeTemplatePhase[] | ProjectTypeTemplatePhase>,
    TemplateApiResponse<unknown>
  >(PATHS.copyPhase(id), data)

export const bulkUpdatePhases = (data: BulkUpdatePhasesData) =>
  request.api.patch<
    TemplateApiResponse<ProjectTypeTemplatePhase[]>,
    TemplateApiResponse<unknown>
  >(PATHS.bulkUpdatePhases, data)
